import React, { useCallback, useEffect, useRef, useState } from 'react';
import { applySyncedScrollLeft, getScrollContentWidth } from '../utils/syncedHorizontalScroll';

/**
 * Обёртка таблицы с дублирующим горизонтальным скроллбаром (сверху или снизу).
 */
const SyncedHorizontalScroll = ({
  children,
  className = '',
  style = {},
  maxHeight,
  contentKey,
  onMainRef,
  scrollbarPosition = 'top',
  'data-testid': testId,
}) => {
  const mainRef = useRef(null);
  const barRef = useRef(null);
  const syncingRef = useRef(false);
  const [spacerWidth, setSpacerWidth] = useState(0);
  const isBottom = scrollbarPosition === 'bottom';

  const setMainNode = useCallback((node) => {
    mainRef.current = node;
    if (typeof onMainRef === 'function') onMainRef(node);
    else if (onMainRef && 'current' in onMainRef) onMainRef.current = node;
  }, [onMainRef]);

  const updateSpacer = useCallback(() => {
    const next = getScrollContentWidth(mainRef.current);
    setSpacerWidth((prev) => (prev === next ? prev : next));
  }, []);

  useEffect(() => {
    updateSpacer();
    const main = mainRef.current;
    if (!main || typeof ResizeObserver === 'undefined') return undefined;

    const ro = new ResizeObserver(() => updateSpacer());
    ro.observe(main);
    if (main.firstElementChild) ro.observe(main.firstElementChild);

    window.addEventListener('resize', updateSpacer);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateSpacer);
    };
  }, [updateSpacer, contentKey, children]);

  const onBarScroll = () => {
    if (syncingRef.current) return;
    applySyncedScrollLeft(mainRef.current, barRef.current?.scrollLeft || 0, syncingRef);
  };

  const onMainScroll = () => {
    if (syncingRef.current) return;
    applySyncedScrollLeft(barRef.current, mainRef.current?.scrollLeft || 0, syncingRef);
  };

  const mainStyle = {
    ...style,
    overflow: 'auto',
    ...(maxHeight != null ? { maxHeight } : {}),
  };

  const barTestId = testId
    ? `${testId}-${isBottom ? 'bottom' : 'top'}-scroll`
    : `synced-h-scroll-${isBottom ? 'bottom' : 'top'}`;

  const scrollBar = (
    <div
      ref={barRef}
      data-testid={barTestId}
      onScroll={onBarScroll}
      style={{
        overflowX: 'auto',
        overflowY: 'hidden',
        height: 14,
        marginBottom: isBottom ? 0 : 2,
        marginTop: isBottom ? 2 : 0,
        flexShrink: 0,
      }}
    >
      <div style={{ width: spacerWidth, height: 1 }} />
    </div>
  );

  return (
    <div className={className} data-testid={testId} style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {!isBottom && scrollBar}
      <div
        ref={setMainNode}
        data-testid={testId ? `${testId}-main-scroll` : 'synced-h-scroll-main'}
        onScroll={onMainScroll}
        style={mainStyle}
      >
        {children}
      </div>
      {isBottom && scrollBar}
    </div>
  );
};

export default SyncedHorizontalScroll;
